import { useEffect, useMemo, useState } from 'react';
import { useGetCaseDetails } from 'src/shared/queries/useGetCaseDetails';
import { useGetCaseQuestionnaires } from 'src/shared/queries/useGetCaseQuestionnaires';
import { useGetCaseHighLevelStatus } from 'src/shared/queries/useGetCaseHighLevelStatus';
import { useGetUser } from 'src/shared/queries/useGetUser';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { identifyAidiContext } from 'src/utils/services/aidiFeatureFlag';
import { ICaseInfo, IQuestionnaire, Stage_Status } from 'src/shared/types/Case';
import { FeatureFlags } from 'src/utils/constants/FeatureFlags';
import { useGetCaseStage } from 'src/shared/queries/useGetCaseStage';
import { AIDI_SUPPORTED_QUESTIONNAIRE_NAMES, GATHERING_DOCS_STAGE_NAME } from '../constants/common';

export enum AidiProgressStatus {
  IDLE = 'idle',
  IN_PROGRESS = 'inprogress',
  COMPLETED = 'completed',
  REOPENED = 'reopened',
}

export interface UseAidiAccessResult {
  loading: boolean;
  error?: unknown;
  // True when (flag && questionnaire && stage) in full mode OR (flag) in flagOnly mode.
  accessGranted?: boolean;
  shouldShowAidiEntry: boolean;
  shouldDisableLegacy: boolean;
  reasons: string[];
  caseDetails?: ICaseInfo;
  questionnaires?: IQuestionnaire[]; // refine type if available
  matchedQuestionnaires: IQuestionnaire[];
  // All questionnaires assigned to the case's employee, AIDI-supported or not. Exposed so callers
  // (e.g. the Gathering Docs task list) can render every assigned questionnaire, each routed to the
  // correct destination, instead of only the AIDI-supported subset.
  employeeQuestionnaires: IQuestionnaire[];
  aidiProgressStatus?: AidiProgressStatus;
  aidiCompletedOn?: string;
  refreshAidiProgress?: () => Promise<void>;
}

interface Params {
  caseId: number | null;
  /**
   * When true, bypass questionnaire + stage gating and rely solely on the feature flag state.
   */
  flagOnly?: boolean;
  /**
   * When true, also derive AIDI progress status (checklist + additional docs) and expose refresh.
   * Defaults to false to avoid extra queries when not needed.
   */
  aidiStatus?: boolean;
}

export function useAidiAccess({ caseId, flagOnly = false, aidiStatus = false }: Params): UseAidiAccessResult {
  const { data: user } = useGetUser();
  const ldClient = useLDClient();
  const flags = useFlags();

  const { data: caseDetails, isLoading: isLoadingCase, error: caseError } = useGetCaseDetails(caseId || 0);
  const {
    data: questionnaires,
    isLoading: isLoadingQuestionnaires,
    error: questionnairesError,
    refetch: refetchCaseQuestionnaires,
  } = useGetCaseQuestionnaires(caseDetails?.caseId || 0);
  const {
    data: stages,
    isLoading: isLoadingStages,
    error: stagesError,
  } = useGetCaseHighLevelStatus(caseDetails?.caseId || 0);

  // Evaluate the feature flag JSON payload against the current case's type and subtypes.
  // Flag shape: Array<{ caseType: number; caseSubtypes: number[] }>
  const flagValue = flags?.[FeatureFlags.FNXAIDIProcess];
  const hasFlag = useMemo(() => {
    if (!Array.isArray(flagValue) || flagValue.length === 0 || !caseDetails) {
      console.log('[useAidiAccess] hasFlag: early exit', {
        flagValue,
        flagIsArray: Array.isArray(flagValue),
        flagLength: Array.isArray(flagValue) ? flagValue.length : 'n/a',
        caseDetails: caseDetails ?? null,
      });
      return false;
    }
    const caseTypeId = caseDetails.caseTypeId;
    const caseSubTypeIds = Array.isArray(caseDetails.associatedCaseSubTypes)
      ? caseDetails.associatedCaseSubTypes.map((c) => Number(c.caseSubTypeId))
      : [];
    const result = flagValue.some(
      (entry: { caseSubtypes: number[]; caseType: number }) =>
        entry.caseType === caseTypeId &&
        Array.isArray(entry.caseSubtypes) &&
        entry.caseSubtypes.some((st) => caseSubTypeIds.includes(st))
    );
    console.log('[useAidiAccess] hasFlag evaluation', {
      flagValue,
      caseTypeId,
      caseSubTypeIds,
      matchingEntries: flagValue.map((entry: { caseSubtypes: number[]; caseType: number }) => ({
        entry,
        caseTypeMatch: entry.caseType === caseTypeId,
        subtypeMatch: Array.isArray(entry.caseSubtypes) && entry.caseSubtypes.some((st) => caseSubTypeIds.includes(st)),
      })),
      result,
    });
    return result;
  }, [flagValue, caseDetails]);

  // Only fetch when both aidiStatus is requested AND feature flag is true to avoid unnecessary network calls.
  const enableAidiProgressFetch = aidiStatus && hasFlag && !!caseDetails?.caseId;
  const {
    data: caseStageResp,
    isLoading: isLoadingCaseStage,
    error: caseStageError,
    refetch: refetchCaseStage,
  } = useGetCaseStage(enableAidiProgressFetch ? caseDetails!.caseId : 0);
  const [didIdentify, setDidIdentify] = useState(false);

  // Fire identification only when caseId changes (cached helper already avoids repeat spamming)
  useEffect(() => {
    if (!ldClient || !user || !caseDetails) return;
    identifyAidiContext(ldClient, { companyId: user.companyId }).then(() => setDidIdentify(true));
  }, [ldClient, user?.companyId, caseDetails?.caseId]);

  const allowRedirectToAIDIBasedOnStage = useMemo(() => {
    if (!stages || stages.length === 0) return false;

    const gatheringIndex = stages.findIndex((s) => s.name.toLowerCase() === GATHERING_DOCS_STAGE_NAME.toLowerCase());
    const inProgressIndex = stages.findIndex(
      (s) => s.status === Stage_Status.STARTED || (s.status === Stage_Status.NOT_STARTED && (s.notes?.length ?? 0) > 0)
    );
    const currentIndex = inProgressIndex >= 0 ? inProgressIndex : stages.length - 1;
    return (
      (gatheringIndex >= 0 && currentIndex >= gatheringIndex) ||
      (!caseDetails?.isActive && stages.length === 1 && stages[0].status === Stage_Status.COMPLETED)
    );
  }, [stages, caseDetails?.isActive]);

  // All questionnaires assigned to this case's employee, regardless of whether AIDI supports them.
  // Computed unconditionally (independent of flagOnly) because the underlying questionnaires query
  // already runs unconditionally, and we need this in flagOnly consumers too to decide whether Legacy
  // access must stay available for questionnaire types AIDI doesn't support (e.g. dynamic questionnaires
  // like "FBI Clearance Questionnaire" assigned by a legal user).
  const employeeQuestionnaires = useMemo((): IQuestionnaire[] => {
    if (isLoadingQuestionnaires || !questionnaires || questionnaires.length === 0 || !caseDetails) return [];
    return questionnaires.filter(
      (q: IQuestionnaire) => q.recipientType === 'EMPLOYEE' && q.recipient === caseDetails.employeeName
    );
  }, [questionnaires, isLoadingQuestionnaires, caseDetails]);

  // Only the subset of employee questionnaires AIDI fully supports today.
  const matchedQuestionnaires = useMemo((): IQuestionnaire[] => {
    if (flagOnly) return [];
    return employeeQuestionnaires.filter((q: IQuestionnaire) => AIDI_SUPPORTED_QUESTIONNAIRE_NAMES.includes(q.questionnaire));
  }, [employeeQuestionnaires, flagOnly]);

  // Any employee questionnaire NOT supported by AIDI (dynamic/legacy-only questionnaires) that must
  // still be completed via the Legacy system, e.g. FBI Clearance Questionnaire assigned by legal.
  const hasLegacyOnlyQuestionnaires = useMemo(
    () => employeeQuestionnaires.some((q: IQuestionnaire) => !AIDI_SUPPORTED_QUESTIONNAIRE_NAMES.includes(q.questionnaire)),
    [employeeQuestionnaires]
  );

  const hasRequiredQuestionnaire = flagOnly || matchedQuestionnaires.length > 0;

  // Flag-only gating vs full gating
  const accessGranted = flagOnly ? hasFlag : hasFlag && hasRequiredQuestionnaire && allowRedirectToAIDIBasedOnStage;
  const shouldShowAidiEntry = accessGranted;
  // Only hide Legacy questionnaire/documents access when there is no other assigned questionnaire type
  // that AIDI doesn't support. This ensures a case with both an AIDI-supported questionnaire (e.g. H-1B
  // Employee QN) and a dynamic/legacy-only questionnaire (e.g. FBI Clearance QN) keeps Legacy reachable.
  const shouldDisableLegacy = accessGranted && !hasLegacyOnlyQuestionnaires;

  // In flagOnly mode we don't need stages to finish loading (only case + identify + flag presence), but we do
  // need questionnaires loaded since shouldDisableLegacy depends on hasLegacyOnlyQuestionnaires in all modes.
  const loading = flagOnly
    ? isLoadingCase || isLoadingQuestionnaires || (!didIdentify && !caseError)
    : isLoadingCase ||
      isLoadingQuestionnaires ||
      isLoadingStages ||
      (enableAidiProgressFetch && isLoadingCaseStage) ||
      (!didIdentify && !caseError);
  const error = caseError || questionnairesError || stagesError || (enableAidiProgressFetch && caseStageError);

  const reasons: string[] = [];
  if (!hasFlag) reasons.push('feature_flag_false');
  if (!flagOnly) {
    if (!hasRequiredQuestionnaire) reasons.push('missing_target_questionnaire');
    if (!allowRedirectToAIDIBasedOnStage) reasons.push('stage_not_reached');
  }
  if (error) reasons.push('fetch_error');
  if (loading) reasons.push('loading');

  // Derive AIDI progress status from the AIDI stage endpoint (proxied via case/stage/:caseId):
  //   - 404 / no response  -> user hasn't started in AIDI (IDLE -> START)
  //   - stage null/empty   -> AIDI knows the case exists but it's not started (IDLE -> START)
  //   - stage "REOPEN_REVIEW_PHASE" -> REOPENED (EDIT FORM)
  //   - stage "completed"  -> COMPLETED (VIEW)
  //   - any other stage    -> IN_PROGRESS (CONTINUE)
  let computedAidiStatus = AidiProgressStatus.IDLE;
  if (enableAidiProgressFetch && caseStageResp?.stage) {
    if (caseStageResp.stage.toLowerCase() === 'reopen_review_phase') {
      computedAidiStatus = AidiProgressStatus.REOPENED;
    } else if (caseStageResp.stage.toLowerCase().includes('completed')) {
      computedAidiStatus = AidiProgressStatus.COMPLETED;
    } else {
      computedAidiStatus = AidiProgressStatus.IN_PROGRESS;
    }
  }

  const refreshAidiProgress = async () => {
    if (!enableAidiProgressFetch) return;
    await Promise.all([refetchCaseStage(), refetchCaseQuestionnaires()]);
  };

  return {
    loading,
    error,
    accessGranted,
    shouldShowAidiEntry,
    shouldDisableLegacy,
    reasons,
    caseDetails,
    questionnaires,
    matchedQuestionnaires,
    employeeQuestionnaires,
    aidiProgressStatus: computedAidiStatus,
    aidiCompletedOn: caseStageResp?.updatedAt,
    refreshAidiProgress,
  };
}
